import axios from 'axios';
import { handleAuthError } from '../../../funciones/authHelper';

/**
 * Wrapper de axios para el módulo Inventario.
 *
 * - Apunta a /api/inventario en el backend.
 * - Añade automáticamente el header Authorization con el JWT de localStorage.
 * - Delega errores de auth (401/403) al helper global del proyecto,
 *   que se encarga de redirigir al login si procede.
 *
 * USO:
 *   import { api } from '../api';
 *
 *   const tipos = await api.get('/tipos');           // GET sin params
 *   const r = await api.get('/eventos', { type: 'asignacion' });  // con query
 *   const nuevo = await api.post('/tipos', { slug, nombre, ... });
 *   const editado = await api.put(`/tipos/${id}`, { nombre });
 *   const r = await api.del(`/eventos/${id}`, { delete_reason: '...' });  // DELETE con body
 *
 *   Todos devuelven el response.data del backend tal cual.
 *   Si hay error, lanzan; el llamante usa try/catch para gestionarlo.
 */

const BASE_URL = `${process.env.REACT_APP_API_URL}/api/inventario`;

// Timeout de red. Sin esto, axios espera indefinidamente: si el
// backend se cuelga (p.ej. un ALTER TABLE esperando un lock de
// metadatos), el frontend se queda "pensando" para siempre. Con el
// timeout, la petición se aborta y el usuario ve un error claro.
// 30s es holgado para operaciones DDL normales (ADD/DROP COLUMN) sobre
// tablas de inventario; si una operación legítima tarda más, es señal
// de un problema (lock) que conviene exponer, no enmascarar.
const REQUEST_TIMEOUT_MS = 30000;

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(method, path, { data, params, base = BASE_URL } = {}) {
  try {
    const response = await axios({
      method,
      url: `${base}${path}`,
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      data,
      params,
    });
    return response.data;
  } catch (error) {
    // Timeout: axios marca error.code === 'ECONNABORTED'. Damos un
    // mensaje accionable en lugar del genérico de axios, para que el
    // usuario sepa que el servidor no respondió (no que "falló el
    // formulario").
    if (error && error.code === 'ECONNABORTED') {
      const e = new Error(
        'El servidor no respondió a tiempo (posible bloqueo de la ' +
          'tabla en la base de datos). Reintenta; si persiste, reinicia ' +
          'el servicio del backend.'
      );
      e.code = 'TIMEOUT';
      e.original = error;
      throw e;
    }
    // Auth errors → helper global gestiona la redirección al login
    handleAuthError(error);
    throw error;
  }
}

export const api = {
  get: (path, params) => request('GET', path, { params }),
  post: (path, data) => request('POST', path, { data }),
  put: (path, data) => request('PUT', path, { data }),
  // PATCH: cambiar el texto de una nota (Fase 2b)
  patch: (path, data) => request('PATCH', path, { data }),
  // DELETE puede llevar body (necesario para soft delete con delete_reason)
  del: (path, data) => request('DELETE', path, { data }),
};

/**
 * Catálogos geográficos (Fase 3b): /api/geo, fuera de /api/inventario. Piden sesión, no permisos.
 *   geo.municipios(provincia) → [{ codigo, nombre, nombre_ine }] vigentes, ordenados por nombre_ine.
 */
const GEO_URL = `${process.env.REACT_APP_API_URL}/api/geo`;
export const geo = {
  municipios: (provincia) => request('GET', '/municipios', { params: { provincia }, base: GEO_URL }),
};
